// ─────────────────────────────────────────────
// src/socket.js  –  front-end  Socket.IO helper
// ─────────────────────────────────────────────
import { io } from 'socket.io-client';

let socket = null;
let currentPage = window.location.pathname;
/* What the user is doing within the current page, for pages that hold several
 * tools behind one route (Global Action Center). Module-level so it survives a
 * reconnect and can ride in the handshake. */
let currentActivity = null;
let activityInterval = null;

/* Resolve URL:
   • DEV:  set REACT_APP_SOCKET_URL=http://127.0.0.1:5000
   • PROD: leave undefined → window.location.origin               */
const SOCKET_URL = '/';

/* Single-ton initialiser
 * SECURITY NOTE: For pen testing compliance, we use HttpOnly cookies exclusively.
 * - HttpOnly cookies CANNOT be read by JavaScript (XSS protection)
 * - The cookie is automatically sent with the WebSocket connection via withCredentials
 * - Server-side authenticates by reading socket.handshake.headers.cookie
 * - Backend sets: httpOnly=true, secure=true (prod), sameSite=None/Lax
 * - NO client-side token handling = secure against XSS attacks
 */
export const initiateSocket = () => {
  if (socket) return;                       // already connected

  // Connect immediately - server will authenticate via HttpOnly cookie
  socket = io(SOCKET_URL, {
    withCredentials: true,                  // ← sends HttpOnly cookie automatically
    transports: ['websocket', 'polling'],
    // Send the current page in the handshake so the server can record it when
    // it creates the session — the connect-time page:navigate event can arrive
    // before the server's listener is attached and get dropped, which left the
    // active-users view showing "Unknown".
    //
    // A FUNCTION, not a plain object: this socket is a singleton created once,
    // so a literal would capture the path at creation and resend that same stale
    // value on every reconnect. The server's reconnect branch assigns the
    // handshake page over the stored one, so a stale value would replace a
    // correct page with the one the user was on when the tab first loaded —
    // confidently wrong, which is worse than "Unknown". Socket.IO v4 calls this
    // before each connection attempt, so reconnects carry the real current page.
    auth: (cb) => cb({ page: window.location.pathname, activity: currentActivity })
  });

  /* Event hooks */
  socket.on('connect', () => {
    console.log('[socket] connected');

    // Send initial page navigation (get current path NOW, not at module load)
    currentPage = window.location.pathname;
    socket.emit('page:navigate', {
      page: currentPage,
      previousPage: null
    });

    // Start activity heartbeat (every 30 seconds)
    if (activityInterval) clearInterval(activityInterval);
    activityInterval = setInterval(() => {
      if (socket?.connected) {
        socket.emit('activity:ping');
      }
    }, 30000);
  });

  socket.on('aggregatedData', data => console.log('[socket] aggregatedData', data));
  socket.on('dataDeltas', dlt => console.log('[socket] dataDeltas', dlt));

  socket.on('connect_error', err => {
    console.error('[socket] error', err.message);
    if (activityInterval) {
      clearInterval(activityInterval);
      activityInterval = null;
    }
  });

  socket.on('disconnect', () => {
    console.log('[socket] disconnected');
    if (activityInterval) {
      clearInterval(activityInterval);
      activityInterval = null;
    }
  });

  // Listen for page navigation
  if (typeof window !== 'undefined') {
    const originalPushState = window.history.pushState;
    window.history.pushState = function(...args) {
      const previousPage = currentPage;
      originalPushState.apply(this, args);
      currentPage = window.location.pathname;
      // The tool belonged to the page being left, so it must not linger and
      // describe the new one.
      currentActivity = null;

      if (socket?.connected) {
        socket.emit('page:navigate', {
          page: currentPage,
          previousPage
        });
      }
    };
  }
};

/* Disconnect socket */
export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  if (activityInterval) {
    clearInterval(activityInterval);
    activityInterval = null;
  }
};

/* Report what the user is doing within the current page.
 *
 * For pages that hold several tools behind one route — Global Action Center has
 * ten — the path alone cannot say whether someone is reading a label list or
 * running a page flip. Pass null when nothing is selected.
 *
 * The value is remembered here as well as emitted, so it rides in the handshake
 * on the next reconnect rather than being lost. */
export const notifyActivity = (activity) => {
  const next = activity || null;
  if (next === currentActivity) return;   // avoid a write per re-render
  currentActivity = next;
  if (socket?.connected) socket.emit('page:activity', { activity: next });
};

/* Get socket instance */
export const getSocket = () => socket;

/* Emit event */
export const emitSocket = (event, payload) => {
  if (socket?.connected) socket.emit(event, payload);
};

/* Log audit event */
export const logAudit = (action, resource, details = {}) => {
  if (socket?.connected) {
    socket.emit('audit:log', {
      action,
      resource,
      details,
      method: details.method,
      endpoint: details.endpoint,
      resourceId: details.resourceId,
      status: details.status || 'success'
    });
  }
};
